/**
 * Preferências visuais da HUD guardadas no navegador (techspec-hud-v3 §19). Como em posicoes.ts: é só
 * aparência, então toda leitura e gravação fica em try/catch e, sem storage, vale o padrão.
 */
const CHAVE_FUNDO_BARRA = 'uoidle.hud.barraFundo';
/** Configurações › Jogabilidade: confirmar antes de atacar um inocente sendo inocente. Padrão ligado. */
const CHAVE_CONFIRMAR_INOCENTE = 'uoidle.jogo.confirmarInocente';

export function lerConfirmarInocente(): boolean {
  try {
    return localStorage.getItem(CHAVE_CONFIRMAR_INOCENTE) !== '0';
  } catch {
    return true;
  }
}

export function gravarConfirmarInocente(ligado: boolean) {
  try { localStorage.setItem(CHAVE_CONFIRMAR_INOCENTE, ligado ? '1' : '0'); } catch { /* sem storage: vale o padrão */ }
}

/**
 * Opacidade do fundo da barra de skills, em %: 100 é a placa cheia, 0 deixa só os slots. Padrão 0 desde 28/10/2026
 * (Renato): a barra nasce transparente para quem nunca mexeu; quem já gravou um valor continua com o dele.
 */
export const FUNDO_BARRA_PADRAO = 0;
export const FUNDO_BARRA_MIN = 0;

export function lerFundoBarra(): number {
  try {
    const v = Number(localStorage.getItem(CHAVE_FUNDO_BARRA));
    if (localStorage.getItem(CHAVE_FUNDO_BARRA) === null || !Number.isFinite(v)) return FUNDO_BARRA_PADRAO;
    return Math.min(100, Math.max(FUNDO_BARRA_MIN, Math.round(v)));
  } catch {
    return FUNDO_BARRA_PADRAO;
  }
}

/** Põe a opacidade na variável CSS `--bb-fundo` (v3-barra.css) e guarda. */
export function aplicarFundoBarra(pct: number, gravar = false) {
  const v = Math.min(100, Math.max(FUNDO_BARRA_MIN, Math.round(pct)));
  document.documentElement.style.setProperty('--bb-fundo', String(v / 100));
  if (!gravar) return;
  try { localStorage.setItem(CHAVE_FUNDO_BARRA, String(v)); } catch { /* sem storage: vale nesta sessão */ }
}

/* ── Barra de skills: tamanho, quantidade de slots e linhas (Configurações › Interface, 28/10/2026) ── */

const CHAVE_BARRA = 'uoidle.hud.barra';
/** Evento da janela: a barra relê as preferências quando o jogador mexe nas Configurações. */
export const EVENTO_BARRA = 'uoidle:barra';

export interface PrefBarra {
  /** Slots de magia/consumível visíveis, de 8 a 16 (a arma não conta). */
  readonly slots: number;
  /** Quebra em duas linhas, metade em cada (a primeira fica com a sobra quando é ímpar). */
  readonly duasLinhas: boolean;
  /** Tamanho em %: 70 a 140, 100 é o de antes. */
  readonly escala: number;
}

export const BARRA_SLOTS_MIN = 8;
export const BARRA_SLOTS_MAX = 16;
export const BARRA_ESCALA_MIN = 70;
export const BARRA_ESCALA_MAX = 140;
export const BARRA_PADRAO: PrefBarra = { slots: 8, duasLinhas: false, escala: 100 };

function limitar(n: unknown, min: number, max: number, padrao: number): number {
  const v = Number(n);
  return Number.isFinite(v) ? Math.min(max, Math.max(min, Math.round(v))) : padrao;
}

export function lerBarra(): PrefBarra {
  try {
    const raw = localStorage.getItem(CHAVE_BARRA);
    if (!raw) return BARRA_PADRAO;
    const o = JSON.parse(raw) as Partial<Record<keyof PrefBarra, unknown>>;
    return {
      slots: limitar(o.slots, BARRA_SLOTS_MIN, BARRA_SLOTS_MAX, BARRA_PADRAO.slots),
      duasLinhas: o.duasLinhas === true,
      escala: limitar(o.escala, BARRA_ESCALA_MIN, BARRA_ESCALA_MAX, BARRA_PADRAO.escala)
    };
  } catch {
    return BARRA_PADRAO;
  }
}

/** Põe a escala na variável CSS `--bb-escala` (v3-barra.css). */
export function aplicarEscalaBarra(pct: number) {
  const v = limitar(pct, BARRA_ESCALA_MIN, BARRA_ESCALA_MAX, 100);
  document.documentElement.style.setProperty('--bb-escala', String(v / 100));
}

/** Grava, aplica a escala e avisa a barra. */
export function gravarBarra(parcial: Partial<PrefBarra>): PrefBarra {
  const atual = lerBarra();
  const prox: PrefBarra = {
    slots: limitar(parcial.slots ?? atual.slots, BARRA_SLOTS_MIN, BARRA_SLOTS_MAX, atual.slots),
    duasLinhas: parcial.duasLinhas ?? atual.duasLinhas,
    escala: limitar(parcial.escala ?? atual.escala, BARRA_ESCALA_MIN, BARRA_ESCALA_MAX, atual.escala)
  };
  try { localStorage.setItem(CHAVE_BARRA, JSON.stringify(prox)); } catch { /* sem storage: vale nesta sessão */ }
  aplicarEscalaBarra(prox.escala);
  window.dispatchEvent(new CustomEvent<PrefBarra>(EVENTO_BARRA, { detail: prox }));
  return prox;
}

/**
 * As linhas da barra: [1..n] numa só, ou metade em cada (16 → 8 + 8; 11 → 6 + 5). Índices dos slots (o 0 é a arma).
 */
export function linhasDaBarra(slots: number, duasLinhas: boolean): number[][] {
  const todos = Array.from({ length: slots }, (_, i) => i + 1);
  if (!duasLinhas) return [todos];
  const primeira = Math.ceil(slots / 2);
  return [todos.slice(0, primeira), todos.slice(primeira)];
}
